import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Globe } from 'lucide-react';
import mcpServerService from '../../services/mcpServerService';
import LintPanel from './LintPanel';
import styles from './McpPublishDialog.module.scss';
import { describeError, isOauthServer } from './mcpUtils';

/**
 * Publish confirmation (§7).
 *
 * `POST /publish` re-lints server-side and refuses on any blocking finding, so
 * a 400 here means the check list moved under us. Its body is only
 * `{"error": "Cannot publish: …"}`, so we show that and lint again — the lint
 * response is what carries `invalid_tools` for the per-tool links.
 *
 * The per-tool actions (open, disable, re-import) close the dialog first: they
 * all lead to another tab.
 */
const McpPublishDialog = ({
  open,
  server,
  lint,
  lintLoading,
  onClose,
  onPublished,
  onRelint,
  onOpenTool,
  onDisableTool,
  onReimportSync,
}) => {
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setError(null);
      onRelint?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const blocked = (lint?.blocking?.length || 0) > 0;
  const closeThen = (fn) => (fn ? (...args) => { onClose(); fn(...args); } : undefined);
  const anonymous = server?.inbound_auth_mode === 'none';
  // Publishing a sign-in server actually fetches the keys URL, so this call
  // takes noticeably longer — and catches a typo that would otherwise 401 every
  // request from the moment it went live.
  const oauth = isOauthServer(server);

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);
    try {
      const result = await mcpServerService.publish(server.id);
      await onPublished?.(result);
    } catch (e) {
      setError(describeError(e, "Couldn't publish this server."));
      if (e?.status === 400) await onRelint?.();
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={publishing ? undefined : onClose}
      maxWidth={false}
      fullWidth
      className={styles.dialog}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(24, 24, 27, 0.4)', backdropFilter: 'blur(2px)' } },
      }}
      PaperProps={{ className: styles.dialogPaper }}
    >
      <DialogTitle className={styles.dialogTitle} component="div">
        <Box className={styles.titleRow}>
          <Globe size={19} />
          <Typography component="h3" variant="h6" className={styles.title}>
            Publish “{server?.name}”
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" className={styles.closeButton} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <Typography variant="body2" className={styles.lead}>
          Publishing makes this server reachable at its public URL. Until then — and again the
          moment you unpublish — the URL returns 404 to everyone.
          {oauth && ' Your keys URL (JWKS) is read as part of this check, so it takes a few seconds.'}
        </Typography>

        {/* Disabling stays in the dialog — the re-lint that follows is what
            un-blocks the Publish button. */}
        <LintPanel
          lint={lint}
          loading={lintLoading}
          dense
          onOpenTool={closeThen(onOpenTool)}
          onDisableTool={onDisableTool}
          onReimportSync={closeThen(onReimportSync)}
        />

        {anonymous && !blocked && (
          <Alert severity="warning">
            This server has no token. Anyone who finds the URL can call it, and crawlers will find
            it. Only publish like this if the data behind it is public.
          </Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button onClick={onClose} variant="text" className={styles.cancelButton} disabled={publishing}>
          Cancel
        </Button>
        <Button
          onClick={handlePublish}
          variant="contained"
          className={styles.saveButton}
          disabled={publishing || blocked}
          startIcon={publishing ? <CircularProgress size={15} color="inherit" /> : null}
        >
          {publishing
            ? oauth
              ? 'Checking your login system…'
              : 'Publishing…'
            : blocked
            ? 'Fix blocking issues first'
            : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default McpPublishDialog;
